import { Snackbar } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';
import { useNavigate, useParams } from 'react-router-dom';
import AcceptTerms from './AcceptTerms';
import './App.css';
import './Case.css';
import CaseSummaryHeader from './CaseSummaryHeader';
import Discovery from './Discovery.js';
import DocumentsOverview from './DocumentsOverview';
import Drafting from './Drafting.js';
import IncidentDetails from './IncidentDetails.js';
import LawAppBar from './LawAppBar';
import LoginScreen from './LoginScreen';
import { handleGoogleLoginSuccess, logout as logoutUtil, renderGoogleLoginButton } from './LoginUtils';
import MedicalChronology from './MedicalChronology.js';
import { caseHasDiscoveryEvidence } from './PathUtils.js';
import MedicalExpenses from './MedicalExpenses.js';
import SourceDocumentViewer from './SourceDocumentViewer.js';
import TimelineView from './TimelineView.js';

function CustomTabPanel(props) {
    const { children, selectedTabValue, myValue, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={selectedTabValue !== myValue}
            id={`simple-tabpanel-${myValue}`}
            aria-labelledby={`simple-tab-${myValue}`}
            {...other}
        >
            {selectedTabValue === myValue && (
                <Box sx={{
                    p: 0
                }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function Case() {
    const { caseId } = useParams();
    const [cookies, setCookie, removeCookie] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);
    // server side state
    const [caseInfo, setCaseInfo] = useState(null);
    const [termsAcceptanceRequired, setTermsAcceptanceRequired] = useState(null);

    // client side state
    const [snackbarMessage, setSnackBarMessage] = useState(null);
    // Contains fields 'color', 'selected', and 'status'.
    const [fileNamesToFilePropertiesMap, setFileNamesToFilePropertiesMap] = useState({});
    const refFileNamesToFilePropertiesMap = useRef();
    // Without using reference, value within fetchCaseInfoAndUpdateUI becomes stale when state
    // is updated from .
    refFileNamesToFilePropertiesMap.current = fileNamesToFilePropertiesMap;

    // Map with filenames that are being uploaded and state has not reached backend DB.
    // Key is filename, value is timestamp until which we wait for 'processing'status to reach DB.
    // We keep uploaded files in this set until the timestamp is reached or until DB status is processing, whichever occurs first.
    const [uploadingFileNamesSet, setUploadingFileNamesSet] = useState({});
    // Index of timeline event that was selected by user(if any)
    const [selectedEventIndex, setSelectedEventIndex] = useState(-1);
    // Name of document that is being displayed in DocumentSourceViewer, if any.
    const [displayedDocument, setDisplayedDocument] = useState(null);
    const [selectedTabValue, setSelectedTabValue] = useState(0);
    const navigate = useNavigate();
    var colorIndex = 0;
    const colors = ['#FF000020', '#50D0D060', '#2F4F4F50', '#D0D05090', '#5050D040', '#50D05070', '#D0505050', '#50505050', '#D050D030'];

    function onGoogleLoginSuccess(response) {
        handleGoogleLoginSuccess(response, setCookie, () => {
            fetchCaseInfoAndUpdateUI();
        });
    }

    function renderLoginButton() {
        renderGoogleLoginButton("googleLogin", onGoogleLoginSuccess);
    }

    function logout() {
        logoutUtil(removeCookie, renderLoginButton);
    }

    useEffect(() => {
        if (cookies.credential && cookies.userid) {
            console.log('In useEffect');
            fetchCaseInfoAndUpdateUI();
            return;
        }
        // LoginScreen component handles button rendering
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefetch document sources when caseInfo is loaded or updated
    useEffect(() => {
        if (!caseInfo || !caseInfo['documents']) {
            return;
        }

        const prefetchLinks = [];

        // Create prefetch links for each document
        for (let docKey in caseInfo['documents']) {
            const docInfo = caseInfo['documents'][docKey];
            const filename = docInfo?.['filename'];

            // Only prefetch if document has been processed and is available
            if (filename && ('pdf' in docInfo || 'txt' in docInfo)) {
                const link = document.createElement('link');
                link.rel = 'prefetch';
                link.href = `/api/documents/${caseInfo['caseId']}/${filename}`;
                link.as = 'document';
                document.head.appendChild(link);
                prefetchLinks.push(link);
            }
        }

        // Cleanup: remove prefetch links when component unmounts or caseInfo changes
        return () => {
            prefetchLinks.forEach(link => {
                if (link.parentNode) {
                    document.head.removeChild(link);
                }
            });
        };
    }, [caseInfo]);

    // console.log('Rendering lawapp with caseInfo: ' + JSON.stringify(caseInfo));
    if (cookies.credential === undefined || cookies.credential === null) {
        return (
            <LoginScreen onGoogleLoginSuccess={onGoogleLoginSuccess} />
        );
    }

    if (termsAcceptanceRequired) {
        return (
            <AcceptTerms
                termsType={termsAcceptanceRequired}
                onTermsAccepted={handleTermsAccepted}
            />
        );
    }

    if (caseInfo === null) {
        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: '100vh',
                background: 'var(--background-color)'
            }}>
                <img
                    src="/loading.gif"
                    alt="loading..."
                    style={{
                        height: "50px",
                        filter: 'opacity(0.8)'
                    }}
                />
            </div>
        );
    }
    let currentTabValue = 0;

    const shouldShowDiscoveryTab = caseHasDiscoveryEvidence(caseInfo)
    //PERSONAL_INJURY_CASE_TYPES.includes((caseInfo['case_type'] || '').toLowerCase());
    const shouldShowIncidentDetailsTab = (caseInfo['documents'] && Object.keys(caseInfo['documents']).some(filename => caseInfo['documents'][filename]['analysis']?.incident_details?.length > 0))
        || (caseInfo['analysis']?.incident_summary?.incident_date && !isNaN(new Date(caseInfo['analysis'].incident_summary.incident_date).getTime()))
        // || COLLISION_REPORT_CASE_TYPES.includes((caseInfo['case_type'] || '').toLowerCase())
        ;
    return (
        <div className="App">
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: "center" }}
                open={snackbarMessage !== null}
                autoHideDuration={3000}
                onClose={() => setSnackBarMessage(null)}
                message={snackbarMessage}
                sx={{
                    '& .MuiSnackbarContent-message': {
                        textAlign: 'center',
                        width: '100%'
                    }
                }}
            />
            <LawAppBar />
            <Box sx={{
                margin: "0 auto",
                padding: "0 20px"
            }}>
                <CaseSummaryHeader
                    caseInfo={caseInfo}
                    setCaseInfo={setCaseInfo}
                    refFileNamesToFilePropertiesMap={refFileNamesToFilePropertiesMap}
                    setFileNamesToFilePropertiesMap={setFileNamesToFilePropertiesMap}
                    uploadingFileNamesSet={uploadingFileNamesSet}
                    onFilesSelectedForUpload={onFilesSelectedForUpload}
                    setSnackBarMessage={setSnackBarMessage}
                />

                <Box sx={{
                    width: '100%',
                    visibility: Object.keys(caseInfo['documents']).length > 0 ? "visible" : "hidden",
                    mt: 4
                }}>
                    <Box sx={{
                        borderBottom: '1px solid var(--border-color)',
                        backgroundColor: 'var(--background-color)',
                        borderRadius: '12px 12px 0 0',
                        overflow: 'hidden'
                    }}>
                        <Tabs
                            variant="scrollable"
                            scrollButtons="auto"
                            value={selectedTabValue}
                            onChange={handleTabChange}
                            aria-label="Information from selected documents"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'var(--accent-color)',
                                    height: '3px'
                                },
                                '& .MuiTab-root': {
                                    color: 'var(--text-secondary)',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    minHeight: '60px',
                                    padding: '12px 24px',
                                    '&.Mui-selected': {
                                        color: 'var(--text-primary)',
                                        fontWeight: 600
                                    },
                                    '&:hover': {
                                        backgroundColor: 'var(--surface-hover)',
                                        color: 'var(--text-primary)'
                                    }
                                }
                            }}
                        >
                            <Tab label="Documents Overview" />
                            {shouldShowIncidentDetailsTab && <Tab label="Incident Details" />}
                            <Tab label="Medical Chronology" />
                            <Tab label="Medical Expenses" />
                            <Tab label="Timeline of Events" />
                            {shouldShowDiscoveryTab && <Tab label="Discovery" />}
                            <Tab label="Drafting" />
                        </Tabs>
                    </Box>
                    <Box sx={{
                        backgroundColor: 'var(--background-color)',
                        border: '1px solid var(--border-color)',
                        borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                        minHeight: '400px'
                    }}>
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <DocumentsOverview caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage} />
                        </CustomTabPanel>
                        {shouldShowIncidentDetailsTab && <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <IncidentDetails caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>}
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <MedicalChronology caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fetchCaseInfoAndUpdateUI={fetchCaseInfoAndUpdateUI}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <MedicalExpenses caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fetchCaseInfoAndUpdateUI={fetchCaseInfoAndUpdateUI}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocument={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <TimelineView caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>
                        {shouldShowDiscoveryTab && <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <Discovery caseInfo={caseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>}
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <Drafting caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                setSnackBarMessage={setSnackBarMessage}
                                setDisplayedDocument={setDisplayedDocument}
                                fetchCaseInfoAndUpdateUI={fetchCaseInfoAndUpdateUI} />
                        </CustomTabPanel>
                    </Box>
                </Box>
            </Box>
            <SourceDocumentViewer caseInfo={caseInfo}
                displayedDocument={displayedDocument}
                sourceDocumentViewerOnClose={sourceDocumentViewerOnClose} />
        </div >
    );

    function handleTabChange(event, newValue) {
        setSelectedTabValue(newValue);
    }

    function sourceDocumentViewerOnClose() {
        setSelectedEventIndex(-1);
        setDisplayedDocument(null);
    }

    function fetchCaseInfoAndUpdateUI() {
        console.log('fetchCaseInfoAndUpdateUI calling: /api/case/' + caseId);

        fetch('/api/case/' + caseId, {
            method: 'GET',
            credentials: 'same-origin'
        })
            .then(res => {
                if (res.status === 401) {
                    logout();
                    return "{}";
                }
                if (res.status === 403) {
                    const termsHeader = res.headers.get('X-Terms-Acceptance-Required');
                    if (termsHeader === 'new-terms') {
                        setTermsAcceptanceRequired(termsHeader);
                        return "{}";
                    }
                    navigate('/mycases');
                    return "{}";
                }
                return res.json();
            })
            .then(json => {
                let newCaseInfo = json?.case;

                if (newCaseInfo) {
                    updateUIWithNewCaseInfo(newCaseInfo);
                } else if (json && Object.keys(json).length > 0) {
                    console.log('FATAL: No caseInfo in response:' + json);
                }
            })
            .catch(err => {
                var data = {
                    err: err,
                    stack: err.stack,
                    context: 'fetchCaseInfoAndUpdateUI'
                }
                fetch('/api/jserrors', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: new Headers({ 'content-type': 'application/json' }),
                    body: JSON.stringify(data)
                });
                console.log('[FATAL]Unable to load case: ' + JSON.stringify(data));
            });
    }

    function handleTermsAccepted() {
        setTermsAcceptanceRequired(null);
        fetchCaseInfoAndUpdateUI();
    }

    function updateUIWithNewCaseInfo(newCaseInfo) {
        console.log('In updateUIWithNewCaseInfo');
        if (caseId === 'new' &&
            /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/.test(newCaseInfo['caseId'])) {
            navigate('/case/' + newCaseInfo['caseId']);
            console.log('Navigating to URL with caseId from /new');
        }
        const newFileNamesToFilePropertiesMap = {};
        colorIndex = 0;
        let hasDocsInProcessingStatus = false;
        for (let filekey in newCaseInfo['documents']) {
            const filename = newCaseInfo['documents'][filekey]['filename'];
            newFileNamesToFilePropertiesMap[filename] = {
                color: colors[colorIndex++],
                selected: (filename in refFileNamesToFilePropertiesMap.current) ? refFileNamesToFilePropertiesMap.current[filename]['selected'] : true,
                status: newCaseInfo['documents'][filekey]['status']
            }
            hasDocsInProcessingStatus = hasDocsInProcessingStatus || newCaseInfo['documents'][filekey]['status']?.includes('processing');
        }

        const newUploadingFileNamesSet = {};
        for (let filename in uploadingFileNamesSet) {
            hasDocsInProcessingStatus = true;
            // Only keep documents in newUploadingFileNamesSet if
            //    it is in DB, but status has not transitioned to 'processing' within allocated time
            if (!(filename in newFileNamesToFilePropertiesMap) || // file is not in DB 
                (!newFileNamesToFilePropertiesMap[filename]['status'].includes('processing') && // status is not 'processing'
                    uploadingFileNamesSet[filename] > new Date().getTime())) { // within allocated time to reach 'processing'
                newUploadingFileNamesSet[filename] = uploadingFileNamesSet[filename];

                newFileNamesToFilePropertiesMap[filename] = {
                    color: colors[colorIndex++],
                    selected: (filename in refFileNamesToFilePropertiesMap.current) ? refFileNamesToFilePropertiesMap.current[filename]['selected'] : true,
                    status: 'processing_ocr'
                }
            }
        }
        setCaseInfo(newCaseInfo);
        setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
        setUploadingFileNamesSet(newUploadingFileNamesSet);
        // console.log('New Case files: ' + Object.keys(newCaseInfo['documents']).join(', '));
        // console.log('Uploading files: ' + Object.keys(newUploadingFileNamesSet).join(', '));

        if (newCaseInfo['analysis_queue']?.length > 0 || hasDocsInProcessingStatus) {
            console.log('analysis_queue length: ' + newCaseInfo['analysis_queue']?.length + '; hasDocsInProcessingStatus:' + hasDocsInProcessingStatus);
            setTimeout(fetchCaseInfoAndUpdateUI, 10000);
        }
    }

    async function onFilesSelectedForUpload(file) {
        console.log('onFilesSelectedForUpload: ' + file[0].name);

        // Log event to Google Analytics
        ReactGA.event('upload_case_documents', {
            file_count: file.length
        });

        let data = new FormData();
        let newFileNamesToFilePropertiesMap = { ...fileNamesToFilePropertiesMap };
        let newUploadingFileNamesSet = { ...uploadingFileNamesSet };
        for (let i = 0; i < file.length; i++) {
            // prepare upload request
            data.append('file', file[i]);

            // reflect in client state
            if (!(file[i].name in newFileNamesToFilePropertiesMap)) { // first time upload
                newFileNamesToFilePropertiesMap[file[i].name] = {
                    color: colors[colorIndex++],
                    selected: true,
                    status: 'processing_ocr'
                }
                newUploadingFileNamesSet[file[i].name] = new Date().getTime();
            } else if (!newFileNamesToFilePropertiesMap[file[i].name]['status'].includes('processing')) { // re-upload same file
                newFileNamesToFilePropertiesMap[file[i].name]['status'] = 'processing_ocr';
                newUploadingFileNamesSet[file[i].name] = new Date().getTime() + 60 * 1000; // give 60 seconds for processing status to be reflected in DB
            }
        }
        setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
        setUploadingFileNamesSet(newUploadingFileNamesSet);

        let response;
        try {
            response = await fetch('/api/documents?caseId=' + caseInfo['caseId'], {
                method: 'POST',
                credentials: 'same-origin',
                body: data,
            });
        } catch (e) {
            console.log('File upload failed: ' + e);
        }
        console.log('Fileupload status: ' + response?.ok + '; Response:' + JSON.stringify(response));
        if (response?.ok) {
            setTimeout(fetchCaseInfoAndUpdateUI, 10000);
        }
    }
}

export default Case;

const COLLISION_REPORT_CASE_TYPES = [
    'car accident',
    'truck accident',
    'motorcycle accident',
    'bicycle accident',
    'bus accident',
    'auto accident',
    'recreational vehicle accident',
    'traffic accident',
]

const PERSONAL_INJURY_CASE_TYPES = [
    'car accident',
    'truck accident',
    'motorcycle accident',
    'bicycle accident',
    'pedestrian accident',
    'boating accident',
    'bus accident',
    'auto accident',
    'train accident',
    'aircraft accident',
    'aviation accident',
    'recreational vehicle accident',
    'injuries from dui',
    'traffic accident',
    'motorcycle insurance claims',
    'insurance claims',
    'slip and fall',
    'dog bite',
    'animal attack',
    'swimming pool accident',
    'elevator accident',
    'escalator accident',
    'inadequate security',
    'negligent security',
    'fire and burn injury',
    'toxic substances exposure',
    'worker\'s compensation',
    'construction accident',
    'construction injury',
    'industrial accident',
    'medical malpractice',
    'birth injuries',
    'nursing home abuse and neglect',
    'pharmaceutical injury',
    'pain and suffering',
    'catastrophic injury',
    'wrongful death',
    'defective product',
    'dangerous drugs',
    'defective medical device',
    'sports and recreational accident',
    'product liability',
    'school and playground injury',
    'child injury',
    'food poisoning',
    'assault',
    'battery',
    'class action lawsuit',
    'brain injury',
    'traumatic brain injury',
    'spinal cord injury',
    'severe burn injury',
    'railroad accident',
    'maritime and jones act',
    'personal injury',
]